import { DashboardAccessRequest } from "../pages/Admin/Requests/dashboardAccessRequestsMockData";
import { MyDashboardAccessRequest } from "../pages/Settings/components/myDashboardAccessRequestsMockData";
import { fetchDataV2, postDataV2 } from "./utils";

// POST/GET .../me (below) are live (docs/needed-endpoints.md §3). The admin-queue GET moved from
// /user_service/dashboard-access-requests to /admin/dashboard-access-requests
// (docs/needed-endpoints.md §11) - same response shape, confirmed against a real example. The
// query param is `status_filter`, not `status` - confirmed via a real 422 ("field required" on
// query.status_filter) when this still sent the old name. The client-side filter below stays as
// a harmless safety net either way.
//
// Approve/reject: live now - confirmed directly against the backend's own route/service code
// (cedar-backend's feat-2/cedar-restructure branch, routes/admin.py + services/user.py's
// update_dashboard_access_requests). The single combined
// POST /admin/dashboard-access-requests/{dashboard_access_id} endpoint §11 already documented
// turned out to have a real, unambiguous contract once actually read - `approval_status`
// ("approved"/"rejected") is a query param, not a body field (same pattern as `status_filter`
// above), and the body is just the optional note. No ViewGroup selector exists or is needed on
// approval - the backend grants the request's own dashboard `internal_view` directly
// (grant_view_access), not a chosen ViewGroup; AdminRequests.tsx's former per-request "access
// level" dropdown was built against the earlier wrong assumption and has been removed since it
// had no real effect on the backend. Response is { status: "success"/"failure", status_code }.
export const getDashboardAccessRequests = (status: DashboardAccessRequest["status"] = "pending"): Promise<DashboardAccessRequest[]> =>
  fetchDataV2(`/admin/dashboard-access-requests?status_filter=${status}`).then((response) =>
    Array.isArray(response) ? response.filter((request) => request.status === status) : []
  );

export interface DashboardAccessRequestDecisionResponse {
  status: "success" | "failure";
  status_code: string;
}

const decideDashboardAccessRequest = (
  id: number,
  approvalStatus: "approved" | "rejected",
  decisionNote?: string
): Promise<DashboardAccessRequestDecisionResponse> =>
  postDataV2(`/admin/dashboard-access-requests/${id}?approval_status=${approvalStatus}`, { note: decisionNote });

export const approveDashboardAccessRequest = (id: number, decisionNote?: string): Promise<DashboardAccessRequestDecisionResponse> =>
  decideDashboardAccessRequest(id, "approved", decisionNote);

export const rejectDashboardAccessRequest = (id: number, decisionNote?: string): Promise<DashboardAccessRequestDecisionResponse> =>
  decideDashboardAccessRequest(id, "rejected", decisionNote);

export const getMyDashboardAccessRequests = (): Promise<MyDashboardAccessRequest[]> =>
  fetchDataV2("/user_service/dashboard-access-requests/me").then((response) => (Array.isArray(response) ? response : []));

// Raw POST response shape - richer than MyDashboardAccessRequest (adds user_id/internal_view, has
// no decided_at yet since a fresh request is never decided) - the hook that calls this normalizes
// it down to MyDashboardAccessRequest before caching it.
export interface SubmittedDashboardAccessRequest {
  id: number;
  user_id: number;
  internal_view: string;
  dashboard_id: number;
  status: MyDashboardAccessRequest["status"];
  requested_at: string;
}

export const submitDashboardAccessRequest = (dashboardId: number): Promise<SubmittedDashboardAccessRequest> =>
  postDataV2("/user_service/dashboard-access-requests", { dashboard_id: dashboardId });
